import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';

interface ConducteursAAACDetailProps {
  t: any;
  isRTL: boolean;
}

const ProductDetailPage = styled.div<{ $isRTL: boolean }>`
  min-height: 100vh;
  background: #f8fafc;
  padding-top: 120px;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  padding-bottom: 4rem;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const Breadcrumbs = styled(motion.div)<{ $isRTL: boolean }>`
  padding: 1rem 0;
  font-size: 0.9rem;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};

  @media (max-width: 768px) {
    font-size: 0.8rem;
    margin-bottom: 1.5rem;
  }
`;

const BreadcrumbLink = styled.span<{ $isLast: boolean }>`
  color: ${props => props.$isLast ? '#dc2626' : '#64748b'};
  text-decoration: none;
  font-weight: ${props => props.$isLast ? '600' : '400'};
  cursor: ${props => props.$isLast ? 'default' : 'pointer'};

  &:hover {
    text-decoration: ${props => props.$isLast ? 'none' : 'underline'};
  }
`;

const ProductLayout = styled.div`
  display: flex;
  gap: 3rem;

  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 2rem;
  }
`;

const ImageSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
`;

const MainImage = styled.div`
  width: 100%;
  max-width: 500px;
  height: 400px;
  background: #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  cursor: pointer;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 12px;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    height: 300px;
  }
`;

const ThumbnailImage = styled.div`
  width: 80px;
  height: 80px;
  background: #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid #dc2626;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 4px;
  }
`;

const TechnicalDatasheet = styled.div<{ $isRTL: boolean }>`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border: none;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  color: inherit;
  width: 100%;
  max-width: 300px;
  
  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem;
    gap: 0.75rem;
    align-self: center;
    margin: 1rem auto;
  }
`;

const DatasheetIcon = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;

  img {
    filter: brightness(0) invert(1);
    width: 24px;
    height: 24px;
  }
`;

const DatasheetText = styled.div<{ $isRTL: boolean }>`
  display: flex;
  flex-direction: column;
  text-align: ${props => props.$isRTL ? 'right' : 'left'};
`;

const DatasheetTitle = styled.span<{ $isRTL: boolean }>`
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const DatasheetFormat = styled.span<{ $isRTL: boolean }>`
  font-size: 0.85rem;
  color: #64748b;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
`;

const ProviderLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border: none;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  width: 100%;
  max-width: 300px;
  
  img {
    max-width: 120px;
    height: auto;
    object-fit: contain;
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem;
    max-width: 250px;
    align-self: center;
    margin: 1rem auto;
    
    img {
      max-width: 100px;
    }
  }
`;

const ProductInfo = styled.div<{ $isRTL: boolean }>`
  flex: 1;
  padding: 1rem 0;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  text-align: ${props => props.$isRTL ? 'right' : 'left'};
`;

const ProductTitle = styled(motion.h1)<{ $isRTL: boolean }>`
  font-size: 2.8rem;
  color: #0f172a;
  font-weight: 700;
  letter-spacing: -0.03em;
  margin-bottom: 1.5rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};

  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
`;

const ContentSection = styled.div<{ $isRTL: boolean }>`
  margin-top: 4rem;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const SectionTitle = styled(motion.h2)<{ $isRTL: boolean }>`
  font-size: 2.2rem;
  color: #0f172a;
  font-weight: 700;
  margin-bottom: 2rem;
  position: relative;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};

  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 60px;
    height: 3px;
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    font-size: 1.8rem;
    margin-bottom: 1.5rem;
  }
`;

const DescriptionSection = styled.div<{ $isRTL: boolean }>`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 20px;
  padding: 3rem;
  margin-bottom: 3rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(220, 38, 38, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  }

  @media (max-width: 768px) {
    padding: 2rem;
    margin-bottom: 2rem;
  }
`;

const DescriptionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const DescriptionCard = styled.div<{ $isRTL: boolean }>`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border-left: 4px solid #dc2626;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const CardTitle = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.3rem;
  color: #0f172a;
  font-weight: 700;
  margin-bottom: 1rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
`;

const CardLabel = styled.div<{ $isRTL: boolean }>`
  font-size: 0.9rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
  font-weight: 600;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
`;

const CardValue = styled.div<{ $isRTL: boolean }>`
  font-size: 1.1rem;
  color: #1e293b;
  font-weight: 600;
  margin-bottom: 1rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
`;

const CardText = styled.p<{ $isRTL: boolean }>`
  font-size: 1rem;
  color: #475569;
  line-height: 1.7;
  margin: 0;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
`;

const CharacteristicsSection = styled.div<{ $isRTL: boolean }>`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 20px;
  padding: 3rem;
  margin-bottom: 3rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(220, 38, 38, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  }

  @media (max-width: 768px) {
    padding: 2rem;
    margin-bottom: 2rem;
  }
`;

const CharacteristicItem = styled.div<{ $isRTL: boolean }>`
  background: white;
  border-radius: 16px;
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: all 0.3s ease;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
`;

const CharacteristicHeader = styled.div<{ $isRTL: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  cursor: pointer;
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  border-bottom: 1px solid #e2e8f0;
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #f1f5f9 0%, #f8fafc 100%);
  }

  @media (max-width: 768px) {
    padding: 1rem 1.5rem;
  }
`;

const CharacteristicTitle = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.2rem;
  color: #0f172a;
  font-weight: 700;
  margin: 0;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
`;

const ChevronIcon = styled.div<{ $isOpen: boolean }>`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #dc2626;
  transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  transition: transform 0.3s ease;
  font-size: 1.2rem;
`;

const CharacteristicContent = styled.div<{ $isOpen: boolean }>`
  max-height: ${props => props.$isOpen ? '1000px' : '0'};
  overflow: hidden;
  transition: max-height 0.4s ease;
`;

const CharacteristicGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  padding: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 1.5rem;
  }
`;

const CharacteristicRow = styled.div<{ $isRTL: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 12px;
  border-left: 3px solid #dc2626;
  transition: all 0.3s ease;

  &:hover {
    background: #f1f5f9;
    transform: translateX(4px);
  }
`;

const CharacteristicLabel = styled.span<{ $isRTL: boolean }>`
  font-size: 0.95rem;
  color: #475569;
  font-weight: 500;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
`;

const CharacteristicValue = styled.span<{ $isRTL: boolean }>`
  font-size: 0.95rem;
  color: #0f172a;
  font-weight: 700;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
`;

const ResourcesSection = styled.div<{ $isRTL: boolean }>`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 20px;
  padding: 3rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(220, 38, 38, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  }

  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const ResourcesContent = styled.div<{ $isRTL: boolean }>`
  background: white;
  padding: 2.5rem;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border-left: 4px solid #dc2626;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 2rem;
    right: 2rem;
    width: 32px;
    height: 32px;
    background-image: url("/images/catalog/ressources.svg");
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    opacity: 0.1;
  }

  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const ResourcesSubTitle = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.4rem;
  color: #0f172a;
  font-weight: 700;
  margin-bottom: 1.5rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
`;

const ResourcesText = styled.p<{ $isRTL: boolean }>`
  font-size: 1.1rem;
  color: #475569;
  line-height: 1.8;
  margin: 0;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
`;

const DocumentationSection = styled.div<{ $isRTL: boolean }>`
  margin-bottom: 2rem;
`;

const DocumentationItem = styled.div<{ $isRTL: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 12px;
  margin-bottom: 1rem;
  transition: all 0.3s ease;

  &:hover {
    background: #f1f5f9;
    transform: translateY(-2px);
  }
`;

const DocumentationInfo = styled.div<{ $isRTL: boolean }>`
  display: flex;
  flex-direction: column;
`;

const DocumentationTitle = styled.span<{ $isRTL: boolean }>`
  font-size: 1rem;
  color: #0f172a;
  font-weight: 600;
  margin-bottom: 0.25rem;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
`;

const DocumentationSize = styled.span<{ $isRTL: boolean }>`
  font-size: 0.9rem;
  color: #64748b;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
`;

const TéléchargerButton = styled.button<{ $isRTL: boolean }>`
  background: #dc2626;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};

  &:hover {
    background: #b91c1c;
    transform: translateY(-1px);
  }
`;

const ConducteursAAACDetail: React.FC<ConducteursAAACDetailProps> = ({ t, isRTL }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const navigate = useNavigate();
  const [openCharacteristics, setOpenCharacteristics] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { label: "Catalogue produits", path: "/" },
    { label: "Transport et distribution de l'énergie", path: "/category/cables-transport" },
    { label: t.overheadLinesTitle, path: "/category/cables-transport" },
    { label: "Câble aérien nu", path: "/category/cables-transport" },
    { label: t.aaacConductorsTitle, path: "/product/conducteurs-aaac" },
  ];

  const product = {
    id: 'conducteurs-aaac',
    title: 'Conducteurs en alliage d\'aluminium (AAAC)',
    image: '/images/catalog/Câbles de transport et de distribution de l\'énergie/Conducteurs en alliage d\'aluminium (AAAC).jpg',
    datasheet: '/images/catalog/Câbles de transport et de distribution de l\'énergie/Conducteurs en alliage d\'aluminium (AAAC)/Conducteurs en alliage d\'aluminium (AAAC).pdf',
  };

  const handleDatasheetClick = () => {
    window.open(product.datasheet, '_blank');
  };

  const handleTélécharger = (filename: string) => {
    const filePath = `/images/catalog/Câbles de transport et de distribution de l'énergie/Conducteurs en alliage d'aluminium (AAAC)/${filename}`;
    window.open(filePath, '_blank');
  };

  const toggleCharacteristic = (key: string) => {
    setOpenCharacteristics(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const characteristics = {
    construction: {
      title: "Caractéristiques de construction",
      items: [
        { label: "Flexibilité de l'âme", value: "Câblé" },
        { label: "Forme de l'âme", value: "Circulaire" },
        { label: "Gaine extérieure", value: "-" },
        { label: "Isolation", value: "-" },
        { label: "Nature de l'âme", value: "Alliage d'aluminium" }
      ]
    },
    dimensional: {
      title: "Caractéristiques dimensionnelles",
      items: [
        { label: "Diamètre des fils", value: "2,5 mm" },
        { label: "Diamètre externe nominal (mm)", value: "12,5 mm" },
        { label: "Diamètre extérieur approximatif", value: "12,5 mm" },
        { label: "Masse approximative", value: "340 kg/km" },
        { label: "Nombre de conducteurs", value: "1" },
        { label: "Nombre total de fils", value: "19" },
        { label: "Section du conducteur", value: "93,3 mm²" }
      ]
    },
    electrical: {
      title: "Caractéristiques électriques",
      items: [
        { label: "Résistance linéique en courant continu à 20° C", value: "0,32 Ohm/km" },
        { label: "Tension de service nominale Uo/U", value: "18 / 30 (36) kV" }
      ]
    },
    mechanical: {
      title: "Caractéristiques mécaniques",
      items: [
        { label: "Charge de rupture calculée", value: "18,5 kN" }
      ]
    },
    usage: {
      title: "Caractéristiques d'utilisation",
      items: [
        { label: "Longueur", value: "4500 m" }
      ]
    }
  };

  return (
    <ProductDetailPage $isRTL={isRTL} ref={ref}>
      <Container>
        <Breadcrumbs 
          $isRTL={isRTL}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={crumb.label}>
              <BreadcrumbLink 
                $isLast={index === breadcrumbs.length - 1} 
                onClick={() => index !== breadcrumbs.length - 1 && navigate(crumb.path)}
              >
                {crumb.label}
              </BreadcrumbLink>
              {index < breadcrumbs.length - 1 && (
                <span>{isRTL ? '<' : '>'}</span>
              )}
            </React.Fragment>
          ))}
        </Breadcrumbs>

        <ProductLayout>
          <ImageSection>
            <MainImage>
              <img src={product.image} alt={product.title} />
            </MainImage>
            <ThumbnailImage>
              <img src={product.image} alt={`${product.title} thumbnail`} />
            </ThumbnailImage>
          </ImageSection>

          <ProductInfo $isRTL={isRTL}>
            <ProductTitle 
              $isRTL={isRTL}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {product.title}
            </ProductTitle>

            <TechnicalDatasheet 
              $isRTL={isRTL}
              onClick={handleDatasheetClick}
            >
              <DatasheetIcon>
                <img 
                  src="/images/catalog/pdf.svg" 
                  alt="PDF icon" 
                />
              </DatasheetIcon>
              <DatasheetText $isRTL={isRTL}>
                <DatasheetTitle $isRTL={isRTL}>Fiche technique</DatasheetTitle>
                <DatasheetFormat $isRTL={isRTL}>PDF</DatasheetFormat>
              </DatasheetText>
            </TechnicalDatasheet>

            <ProviderLogo>
              <img 
                src="/images/partners/nexans.png" 
                alt="Nexans" 
              />
            </ProviderLogo>
          </ProductInfo>
        </ProductLayout>

        <ContentSection $isRTL={isRTL}>
          <SectionTitle 
            $isRTL={isRTL}
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Description
          </SectionTitle>
          <DescriptionSection $isRTL={isRTL}>
            <DescriptionGrid>
              <DescriptionCard $isRTL={isRTL}>
                <CardTitle $isRTL={isRTL}>Normes</CardTitle>
                <CardLabel $isRTL={isRTL}>TEST</CardLabel>
                <CardValue $isRTL={isRTL}>NF C34-125</CardValue>
              </DescriptionCard>
              
              <DescriptionCard $isRTL={isRTL}>
                <CardTitle $isRTL={isRTL}>Application</CardTitle>
                <CardText $isRTL={isRTL}>
                  Conducteurs pour lignes de distribution aériennes longue portée.
                </CardText>
              </DescriptionCard>
              
              <DescriptionCard $isRTL={isRTL}>
                <CardTitle $isRTL={isRTL}>Utilisation</CardTitle>
                <CardText $isRTL={isRTL}>
                  Ces conducteurs homogènes en alliage d'aluminium font preuve de nombreux avantages : la légèreté, une charge de rupture haute permettant de longues portées, la simplicité des accessoires et enfin le recyclage facile. Ces conducteurs conviennent aux réseaux de longue portée.
                </CardText>
              </DescriptionCard>
            </DescriptionGrid>
          </DescriptionSection>

          <SectionTitle 
            $isRTL={isRTL}
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            Caractéristiques
          </SectionTitle>
          <CharacteristicsSection $isRTL={isRTL}>
            {Object.entries(characteristics).map(([key, characteristic]) => (
              <CharacteristicItem key={key} $isRTL={isRTL}>
                <CharacteristicHeader 
                  $isRTL={isRTL}
                  onClick={() => toggleCharacteristic(key)}
                >
                  <CharacteristicTitle $isRTL={isRTL}>
                    {characteristic.title}
                  </CharacteristicTitle>
                  <ChevronIcon $isOpen={openCharacteristics[key]}>
                    ▼
                  </ChevronIcon>
                </CharacteristicHeader>
                <CharacteristicContent $isOpen={openCharacteristics[key]}>
                  <CharacteristicGrid>
                    {characteristic.items.map((item, index) => (
                      <CharacteristicRow key={index} $isRTL={isRTL}>
                        <CharacteristicLabel $isRTL={isRTL}>
                          {item.label}
                        </CharacteristicLabel>
                        <CharacteristicValue $isRTL={isRTL}>
                          {item.value}
                        </CharacteristicValue>
                      </CharacteristicRow>
                    ))}
                  </CharacteristicGrid>
                </CharacteristicContent>
              </CharacteristicItem>
            ))}
          </CharacteristicsSection>

          <SectionTitle 
            $isRTL={isRTL}
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            Ressources
          </SectionTitle>
          <ResourcesSection $isRTL={isRTL}>
            <ResourcesContent $isRTL={isRTL}>
              <DocumentationSection $isRTL={isRTL}>
                <ResourcesSubTitle $isRTL={isRTL}>Documentation</ResourcesSubTitle>
                <DocumentationItem $isRTL={isRTL}>
                  <DocumentationInfo $isRTL={isRTL}>
                    <DocumentationTitle $isRTL={isRTL}>Catalogue Aérien</DocumentationTitle>
                    <DocumentationSize $isRTL={isRTL}>PDF — 2.8 MB</DocumentationSize>
                  </DocumentationInfo>
                  <TéléchargerButton 
                    $isRTL={isRTL}
                    onClick={() => handleTélécharger('Catalogue Aérien.pdf')}
                  >
                    Télécharger
                  </TéléchargerButton>
                </DocumentationItem>
              </DocumentationSection>

              <ResourcesSubTitle $isRTL={isRTL}>Information de livraison</ResourcesSubTitle>
              <ResourcesText $isRTL={isRTL}>
                Techyid dispose d'une gamme de conducteurs importante. N'hésitez pas à nous contacter pour trouver celui qui répond exactement à vos besoins.
              </ResourcesText>
            </ResourcesContent>
          </ResourcesSection>
        </ContentSection>
      </Container>
    </ProductDetailPage>
  );
};

export default ConducteursAAACDetail;
